import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Heart, Star, CheckCircle, XCircle, User, Moon, Check, X, Download, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { memo, useState } from 'react'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

export type DetailScoreObject = {
  status?: string
  score: number
  max?: number
  is_bool?: boolean
}

export type DetailItem = string | number | boolean | DetailScoreObject

export type CompatibilityPersonInfo = {
  nakshatra: string
  pada: string
  rasi?: string
}

export type NadiAnalysis = {
  dosha_present: boolean
  is_cancelled?: boolean
  cancellation_reason?: string
}

export type CompatibilityScores = {
  details_south: Record<string, DetailItem>
  total_score_south: number
  is_compatible_south: boolean
  details_north?: Record<string, DetailItem> | null
  total_score_north: number
  nadi_analysis?: NadiAnalysis | null
}

export type ManglikPerson = {
  present?: boolean
  cancelled?: boolean
  cancellation_codes?: number[]
  effective?: boolean
  error?: string
}

export type ManglikAnalysis = {
  boy?: ManglikPerson
  girl?: ManglikPerson
  pair?: {
    both_effective: boolean
    either_effective: boolean
  }
}

export type PapaSamyamBreakdownItem = {
  planet: string
  planet_id?: number
  anchor: string
  anchor_weight?: number
  house_position: number
  house_weight?: number
  graha_weight?: number
  sign: number
  sign_name: string
  base_score: number
  mitigation_multiplier: number
  mitigation_basis?: string | null
  apavada_triggered?: string[] | null
  final_score: number
}

export type PapaSamyamPersonReport = {
  total_dosha: number
  breakdown: PapaSamyamBreakdownItem[]
  jupiter_gate?: unknown
  moon_gate?: unknown
  venus_gate?: unknown
  error?: string
}

export type PapaSamyamPair = {
  boy?: PapaSamyamPersonReport | { error: string }
  girl?: PapaSamyamPersonReport | { error: string }
  match?: {
    approved: boolean
    rejected: boolean
    warning_groom_afflictions_high: boolean
  }
}

export type DoshaAnalysis = {
  manglik?: ManglikAnalysis
  papa_samyam?: PapaSamyamPair
}

export type CompatibilityData = {
  boy_info: CompatibilityPersonInfo
  girl_info: CompatibilityPersonInfo
  compatibility_scores: CompatibilityScores
  dosha_analysis?: DoshaAnalysis
}

interface CompatibilityDisplayProps {
  data: CompatibilityData
  onReset: () => void
  onDiscussInAstro?: () => void
}

const isDetailScoreObject = (value: DetailItem): value is DetailScoreObject => {
  return typeof value === 'object' && value !== null && 'score' in value
}

const isPapaSamyamPersonReport = (
  value: PapaSamyamPersonReport | { error: string } | undefined
): value is PapaSamyamPersonReport => {
  return Boolean(value && 'total_dosha' in value && Array.isArray((value as PapaSamyamPersonReport).breakdown))
}

const sanitizeFilenamePart = (value?: string | null) => {
  const cleaned = String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return cleaned || 'match'
}

const getCompatibilityPdfFilename = (data: CompatibilityData) => {
  const boy = sanitizeFilenamePart(data?.boy_info?.nakshatra || 'boy')
  const girl = sanitizeFilenamePart(data?.girl_info?.nakshatra || 'girl')
  const date = new Date().toISOString().slice(0, 10)
  return `compatibility-${boy}-${girl}-${date}.pdf`
}

const CompatibilityDisplay = memo(({ data, onReset, onDiscussInAstro }: CompatibilityDisplayProps) => {
  const { t, i18n } = useTranslation('chat')
  const { boy_info, girl_info, compatibility_scores } = data
  const { details_south, total_score_south, is_compatible_south, details_north, total_score_north, nadi_analysis } = compatibility_scores
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const doshaAnalysis = data?.dosha_analysis
  const manglik = doshaAnalysis?.manglik
  const papaSamyam = doshaAnalysis?.papa_samyam

  const handleDownloadPDF = async () => {
    if (isGeneratingPdf) return

    setIsGeneratingPdf(true)

    try {
      const [{ pdf }, { default: CompatibilityPDFDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./CompatibilityPDFDocument'),
      ])
      const blob = await pdf(
        <CompatibilityPDFDocument
          data={data}
          locale={i18n.language}
          reportTitle={t('chat.compatibility_ui.marriage_compatibility_report')}
          generatedBy={t('chat.compatibility_ui.generated_by_sarvalok')}
          labels={{
            boyDetails: t('chat.compatibility_form.boy_details'),
            girlDetails: t('chat.compatibility_form.girl_details'),
            nakshatra: t('chat.compatibility_ui.nakshatra'),
            pada: t('chat.compatibility_ui.pada'),
            rasi: t('chat.compatibility_ui.rasi'),
            southIndian: t('chat.compatibility_ui.south_indian'),
            northIndian: t('chat.compatibility_ui.north_indian'),
            totalScore: t('chat.compatibility_ui.total_score'),
            verdict: t('chat.compatibility_ui.verdict'),
            compatible: t('chat.compatibility_ui.compatible'),
            notCompatible: t('chat.compatibility_ui.not_compatible'),
            goodMatch: t('chat.compatibility_ui.good_match'),
            belowAverage: t('chat.compatibility_ui.below_average'),
            detailedAnalysis: t('chat.compatibility_ui.detailed_analysis'),
            ashtakootaDetails: t('chat.compatibility_ui.ashtakoota_details'),
            northDetailsUnavailable: t('chat.compatibility_ui.north_details_unavailable'),
            nadiPassCancelled: t('chat.compatibility_ui.nadi_pass_cancelled'),
            nadiCriticalAlert: t('chat.compatibility_ui.nadi_critical_alert'),
            nadiAlertDescription: t('chat.compatibility_ui.nadi_alert_description'),
            matchConsideredAuspicious: t('chat.compatibility_ui.match_considered_auspicious'),
            exceptionCheck: t('chat.compatibility_ui.exception_check'),
            nadiFailNoException: t('chat.compatibility_ui.nadi_fail_no_exception'),
            good: t('chat.compatibility_ui.good'),
            bad: t('chat.compatibility_ui.bad'),
            doshaSectionTitle: t('chat.compatibility_dosha.section_title', 'Dosha Analysis (Vedic)'),
            doshaSectionSubtitle: t('chat.compatibility_dosha.section_subtitle', 'Reported separately from Ashtakoota totals.'),
            manglikBoyTitle: t('chat.compatibility_dosha.manglik_boy_title', 'Manglik / Kuja Dosha (Boy)'),
            manglikGirlTitle: t('chat.compatibility_dosha.manglik_girl_title', 'Manglik / Kuja Dosha (Girl)'),
            statusLabel: t('chat.compatibility_dosha.status_label', 'Status'),
            statusPresentNotCancelled: t('chat.compatibility_dosha.status_present_not_cancelled', 'Present (Not Cancelled)'),
            statusPresentCancelled: t('chat.compatibility_dosha.status_present_cancelled', 'Present (Cancelled)'),
            statusNotPresent: t('chat.compatibility_dosha.status_not_present', 'Not Present'),
            papaTitle: t('chat.compatibility_dosha.papa_title', 'Papa Samyam / Dosha Samyam'),
            papaRule: t('chat.compatibility_dosha.papa_rule', 'Hard rule: Bride total must not exceed Groom total (Bride ≤ Groom).'),
            approved: t('chat.compatibility_dosha.approved', 'Approved'),
            rejected: t('chat.compatibility_dosha.rejected', 'Rejected'),
            groomTotal: t('chat.compatibility_dosha.groom_total', 'Groom Total'),
            brideTotal: t('chat.compatibility_dosha.bride_total', 'Bride Total'),
            warningGroomHigh: t('chat.compatibility_dosha.warning_groom_high', 'Match is acceptable regarding Papa Samyam, but the Groom’s afflictions significantly outweigh the Bride’s (soft guideline).'),
            breakdownGroom: t('chat.compatibility_dosha.breakdown_groom', 'Groom Breakdown'),
            breakdownBride: t('chat.compatibility_dosha.breakdown_bride', 'Bride Breakdown'),
            breakdownIntroGroom: t('chat.compatibility_dosha.breakdown_intro_groom', 'Each row shows the shastric calculation for one malefic placement (anchor × house × planet), then reduced/neutralized by apavada and mitigating aspects.'),
            breakdownIntroBride: t('chat.compatibility_dosha.breakdown_intro_bride', 'Explanations translate technical rules into plain English (e.g., strong Jupiter aspect neutralizes a dosha).'),
            noItems: t('chat.compatibility_dosha.no_items', 'No items.'),
            mitigated: t('chat.compatibility_dosha.badge_mitigated', 'Mitigated'),
            tablePlanet: t('chat.compatibility_dosha.table.planet', 'Planet'),
            tableAnchor: t('chat.compatibility_dosha.table.anchor', 'Anchor'),
            tableHouse: t('chat.compatibility_dosha.table.house', 'House'),
            tableSign: t('chat.compatibility_dosha.table.sign', 'Sign'),
            tableBase: t('chat.compatibility_dosha.table.base', 'Base'),
            tableMultiplier: t('chat.compatibility_dosha.table.multiplier', 'x Mult'),
            tableFinal: t('chat.compatibility_dosha.table.final', 'Final'),
            tableExplanation: t('chat.compatibility_dosha.table.explanation', 'Explanation'),
          }}
        />
      ).toBlob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = getCompatibilityPdfFilename(data)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 2000)
    } catch (error) {
      console.error('Failed to generate compatibility PDF', error)
      toast.error(t('chat.compatibility_ui.pdf_error', 'Could not create the compatibility PDF. Please try again.'), {
        position: 'bottom-right',
      })
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 20) return 'text-green-600'
    if (score >= 10) return 'text-yellow-600'
    return 'text-red-600'
  }

  const renderNadiAlert = () => {
    if (!nadi_analysis || !nadi_analysis.dosha_present) return null
    
    if (nadi_analysis.is_cancelled) {
        return (
            <div className="mt-6 p-4 rounded-md bg-green-50 border border-green-200">
                <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <div>
                        <h4 className="font-semibold text-green-800">{t('chat.compatibility_ui.nadi_pass_cancelled')}</h4>
                        <p className="text-green-700 text-sm mt-1">
                            {nadi_analysis.cancellation_reason} {t('chat.compatibility_ui.match_considered_auspicious')}
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="mt-6 p-4 rounded-md bg-red-50 border border-red-200">
            <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                <div>
                    <h4 className="font-semibold text-red-800">{t('chat.compatibility_ui.nadi_critical_alert')}</h4>
                    <p className="text-red-700 text-sm mt-1">
                        {t('chat.compatibility_ui.nadi_alert_description')}
                    </p>
                    <div className="mt-3 pt-3 border-t border-red-200">
                        <p className="text-red-800 text-xs font-semibold uppercase tracking-wide mb-1">{t('chat.compatibility_ui.exception_check')}</p>
                        <p className="text-red-700 text-sm">
                            {t('chat.compatibility_ui.nadi_fail_no_exception')}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
  }

  const renderDetailRow = (label: string, item: DetailItem) => {
    if (!isDetailScoreObject(item)) {
         return (
            <div className="flex justify-between items-center py-3 border-b last:border-0 border-gray-100">
              <span className="text-gray-700 font-medium capitalize">{label.replace(/_/g, ' ')}</span>
              <span className="font-semibold text-gray-900">{String(item)}</span>
            </div>
         )
    }

    const { status, score, max = 0, is_bool } = item
    
    return (
      <div className="flex justify-between items-center py-3 border-b last:border-0 border-gray-100 group hover:bg-gray-50 px-2 rounded-md transition-colors">
        <span className="text-gray-700 font-medium capitalize">{label.replace(/_/g, ' ')}</span>
        <div className="flex items-center gap-2">
            {is_bool ? (
                status === 'Good' || score === 1 ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Check className="w-3 h-3 mr-1" /> {t('chat.compatibility_ui.good')}
                    </span>
                ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <X className="w-3 h-3 mr-1" /> {t('chat.compatibility_ui.bad')}
                    </span>
                )
            ) : (
                <div className="flex items-baseline gap-1">
                    <span className="font-bold text-gray-900">{score}</span>
                    {max > 0 && <span className="text-xs text-gray-400">/ {max}</span>}
                </div>
            )}
        </div>
      </div>
    )
  }

  const renderManglikRow = (label: string, value?: ManglikPerson) => {
    if (!value) return null
    const present = Boolean(value.present)
    const cancelled = Boolean(value.cancelled)
    const effective = typeof value.effective === 'boolean' ? value.effective : (present && !cancelled)
    const status = effective
      ? t('chat.compatibility_dosha.status_present_not_cancelled', 'Present (Not Cancelled)')
      : (present
        ? t('chat.compatibility_dosha.status_present_cancelled', 'Present (Cancelled)')
        : t('chat.compatibility_dosha.status_not_present', 'Not Present'))
    const badgeClass = effective ? 'bg-red-100 text-red-800 border-red-200' : (present ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-700 border-gray-200')
    return (
      <div className="flex justify-between items-center py-2 border-b last:border-0 border-gray-100">
        <span className="text-gray-700 font-medium">{label}</span>
        <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${badgeClass}`}>
          {effective ? <XCircle className="w-3.5 h-3.5 mr-1" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
          {status}
        </span>
      </div>
    )
  }

  const renderPapaSamyamTable = (breakdown?: PapaSamyamBreakdownItem[]) => {
    if (!Array.isArray(breakdown) || breakdown.length === 0) {
      return <div className="text-sm text-gray-500">{t('chat.compatibility_dosha.no_items', 'No items.')}</div>
    }
    const explainToken = (token: string) => {
      const tok = String(token || '')
      if (tok.startsWith('Kuja_Exception_Code_')) return `${t('chat.compatibility_dosha.explain.kuja_exception_prefix', 'Kuja apavada exception')} (${tok.replace('Kuja_Exception_Code_', '')})`
      if (tok === 'Guru_Yuti') return t('chat.compatibility_dosha.explain.guru_yuti', 'Neutralized by conjunction with a strong Jupiter')
      if (tok === 'Guru_Drishti') return t('chat.compatibility_dosha.explain.guru_drishti', 'Neutralized by protective aspect from a strong Jupiter')
      if (tok === 'Kuja_Manglik_Exception') return t('chat.compatibility_dosha.explain.kuja_manglik_exception', 'Neutralized by Kuja apavada (Manglik exception rules)')
      if (tok === 'Kuja_Neecha_Cancer') return t('chat.compatibility_dosha.explain.kuja_neecha_cancer', 'Neutralized: Mars debilitated in Cancer')
      if (tok === 'Kuja_Safe_Sign') return t('chat.compatibility_dosha.explain.kuja_safe_sign', 'Neutralized: Mars in a safe sign (Papa Samyam)')
      if (tok === 'Shani_12th_Exempt') return t('chat.compatibility_dosha.explain.shani_12th_exempt', 'Neutralized: Saturn 12th exemption')
      if (tok === 'Nodes_2nd_Gemini_Virgo') return t('chat.compatibility_dosha.explain.nodes_2nd_gemini_virgo', 'Neutralized: Rahu/Ketu in 2nd (Gemini/Virgo)')
      if (tok === 'OwnOrExalted') return t('chat.compatibility_dosha.explain.own_or_exalted', 'Neutralized by own sign or exaltation')
      if (tok === 'Panchadha_Mitra') return t('chat.compatibility_dosha.explain.panchadha_mitra', 'Mitigated by Panchadha Maitri (friend)')
      if (tok === 'Panchadha_Sama') return t('chat.compatibility_dosha.explain.panchadha_sama', 'Mitigated by Panchadha Maitri (neutral)')
      if (tok === 'Panchadha_ShatruOrWorse') return t('chat.compatibility_dosha.explain.panchadha_shatru_or_worse', 'No mitigation (enemy/great enemy)')
      if (tok === 'Shukra_Drishti') return t('chat.compatibility_dosha.explain.shukra_drishti', 'Mitigated by Venus 7th aspect')
      if (tok === 'Chandra_Drishti') return t('chat.compatibility_dosha.explain.chandra_drishti', 'Mitigated by strong Moon 7th aspect')
      if (tok === 'Apavada') return t('chat.compatibility_dosha.explain.apavada', 'Neutralized by apavada (exception)')
      return tok
    }
    const explainRow = (row: PapaSamyamBreakdownItem) => {
      const ap = Array.isArray(row?.apavada_triggered) ? row.apavada_triggered : []
      if (ap.length > 0) return ap.map(explainToken).join('; ')
      const basis = String(row?.mitigation_basis || '')
      if (!basis) return ''
      return basis.split('+').filter(Boolean).map(explainToken).join('; ')
    }
    return (
      <div className="overflow-x-auto">
        <table className="min-w-[780px] w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
              <th className="py-2 pr-3">{t('chat.compatibility_dosha.table.planet', 'Planet')}</th>
              <th className="py-2 pr-3">{t('chat.compatibility_dosha.table.anchor', 'Anchor')}</th>
              <th className="py-2 pr-3">{t('chat.compatibility_dosha.table.house', 'House')}</th>
              <th className="py-2 pr-3">{t('chat.compatibility_dosha.table.sign', 'Sign')}</th>
              <th className="py-2 pr-3">{t('chat.compatibility_dosha.table.base', 'Base')}</th>
              <th className="py-2 pr-3">{t('chat.compatibility_dosha.table.multiplier', '× Mult')}</th>
              <th className="py-2 pr-3">{t('chat.compatibility_dosha.table.final', 'Final')}</th>
              <th className="py-2 pr-3">{t('chat.compatibility_dosha.table.explanation', 'Explanation')}</th>
            </tr>
          </thead>
          <tbody>
            {breakdown.map((row, idx: number) => (
              <tr key={idx} className="border-b last:border-0">
                <td className="py-2 pr-3 font-medium text-gray-900">{row.planet}</td>
                <td className="py-2 pr-3 text-gray-700">{row.anchor}</td>
                <td className="py-2 pr-3 text-gray-700">{row.house_position}</td>
                <td className="py-2 pr-3 text-gray-700">{row.sign_name}</td>
                <td className="py-2 pr-3 text-gray-700">{Number(row.base_score ?? 0).toFixed(3)}</td>
                <td className="py-2 pr-3 text-gray-700">{Number(row.mitigation_multiplier ?? 0).toFixed(2)}</td>
                <td className="py-2 pr-3 font-semibold text-gray-900">
                  <div className="flex items-center gap-2">
                    <span>{Number(row.final_score ?? 0).toFixed(3)}</span>
                    {(Array.isArray(row?.apavada_triggered) && row.apavada_triggered.length > 0) || Number(row?.mitigation_multiplier ?? 1) < 1 ? (
                      <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                        {t('chat.compatibility_dosha.badge_mitigated', 'Mitigated')}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="py-2 pr-3 text-gray-600">{explainRow(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const renderDoshaSection = () => {
    const hasManglik = manglik && typeof manglik === 'object'
    const hasPapa = papaSamyam && typeof papaSamyam === 'object'
    if (!hasManglik && !hasPapa) return null

    const boyPapa = isPapaSamyamPersonReport(papaSamyam?.boy) ? papaSamyam?.boy : undefined
    const girlPapa = isPapaSamyamPersonReport(papaSamyam?.girl) ? papaSamyam?.girl : undefined
    const match = papaSamyam?.match
    const boyTotal = typeof boyPapa?.total_dosha === 'number' ? boyPapa.total_dosha : null
    const girlTotal = typeof girlPapa?.total_dosha === 'number' ? girlPapa.total_dosha : null
    const rejected = Boolean(match?.rejected)
    const approved = Boolean(match?.approved)
    const warn = Boolean(match?.warning_groom_afflictions_high)

    return (
      <Card className="border-2 border-amber-100 overflow-hidden">
        <div className="bg-amber-50 p-6 border-b border-amber-100">
          <h3 className="text-lg font-semibold text-amber-900 flex items-center gap-2">
            <Heart className="w-5 h-5 text-amber-700" />
            {t('chat.compatibility_dosha.section_title', 'Dosha Analysis (Vedic)')}
          </h3>
          <p className="text-sm text-amber-800/80 mt-1">{t('chat.compatibility_dosha.section_subtitle', 'Reported separately from Ashtakoota totals.')}</p>
        </div>
        <CardContent className="p-6 space-y-6">
          {hasManglik && (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="font-semibold text-gray-900 mb-2">{t('chat.compatibility_dosha.manglik_boy_title', 'Manglik / Kuja Dosha (Boy)')}</div>
                {renderManglikRow(t('chat.compatibility_dosha.status_label', 'Status'), manglik?.boy)}
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="font-semibold text-gray-900 mb-2">{t('chat.compatibility_dosha.manglik_girl_title', 'Manglik / Kuja Dosha (Girl)')}</div>
                {renderManglikRow(t('chat.compatibility_dosha.status_label', 'Status'), manglik?.girl)}
              </div>
            </div>
          )}

          {hasPapa && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="font-semibold text-gray-900">{t('chat.compatibility_dosha.papa_title', 'Papa Samyam / Dosha Samyam')}</div>
                  <div className="text-sm text-gray-600">{t('chat.compatibility_dosha.papa_rule', 'Hard rule: Bride total must not exceed Groom total (Bride ≤ Groom).')}</div>
                </div>
                <div className="flex items-center gap-2">
                  {approved && (
                    <span className="inline-flex items-center rounded-full border border-green-200 bg-green-100 px-3 py-1 text-sm font-semibold text-green-800">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      {t('chat.compatibility_dosha.approved', 'Approved')}
                    </span>
                  )}
                  {rejected && (
                    <span className="inline-flex items-center rounded-full border border-red-200 bg-red-100 px-3 py-1 text-sm font-semibold text-red-800">
                      <XCircle className="w-4 h-4 mr-1" />
                      {t('chat.compatibility_dosha.rejected', 'Rejected')}
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-4 grid md:grid-cols-2 gap-4">
                <div className="p-3 rounded-md bg-gray-50 border border-gray-100">
                  <div className="text-xs uppercase tracking-wide text-gray-500">{t('chat.compatibility_dosha.groom_total', 'Groom Total')}</div>
                  <div className="text-2xl font-bold text-gray-900">{boyTotal !== null ? boyTotal.toFixed(3) : '—'}</div>
                </div>
                <div className="p-3 rounded-md bg-gray-50 border border-gray-100">
                  <div className="text-xs uppercase tracking-wide text-gray-500">{t('chat.compatibility_dosha.bride_total', 'Bride Total')}</div>
                  <div className="text-2xl font-bold text-gray-900">{girlTotal !== null ? girlTotal.toFixed(3) : '—'}</div>
                </div>
              </div>

              {warn && (
                <div className="mt-4 p-4 rounded-md bg-yellow-50 border border-yellow-200 text-yellow-900 text-sm">
                  {t('chat.compatibility_dosha.warning_groom_high', 'Match is acceptable regarding Papa Samyam, but the Groom’s afflictions significantly outweigh the Bride’s (soft guideline).')}
                </div>
              )}

              <div className="mt-5">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="boy">
                    <AccordionTrigger>{t('chat.compatibility_dosha.breakdown_groom', 'Groom Breakdown')}</AccordionTrigger>
                    <AccordionContent>
                      <div className="text-sm text-gray-600 mb-3">
                        {t('chat.compatibility_dosha.breakdown_intro_groom', 'Each row shows the shastric calculation for one malefic placement (anchor × house × planet), then reduced/neutralized by apavada and mitigating aspects.')}
                      </div>
                      {renderPapaSamyamTable(boyPapa?.breakdown)}
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="girl">
                    <AccordionTrigger>{t('chat.compatibility_dosha.breakdown_bride', 'Bride Breakdown')}</AccordionTrigger>
                    <AccordionContent>
                      <div className="text-sm text-gray-600 mb-3">
                        {t('chat.compatibility_dosha.breakdown_intro_bride', 'Explanations translate technical rules into plain English (e.g., strong Jupiter aspect neutralizes a dosha).')}
                      </div>
                      {renderPapaSamyamTable(girlPapa?.breakdown)}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6" id="compat-report">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Boy Info */}
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
            <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
                    <User className="h-5 w-5 text-blue-600" />
                    {t('chat.compatibility_form.boy_details')}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-500">{t('chat.compatibility_ui.nakshatra')}</span>
                    <span className="text-lg font-bold text-blue-700">{boy_info.nakshatra}</span>
                </div>
                <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-500">{t('chat.compatibility_ui.pada')}</span>
                    <span className="text-sm font-medium text-gray-700">{boy_info.pada}</span>
                </div>
                {boy_info.rasi && (
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 flex items-center gap-1"><Moon className="w-3 h-3" /> {t('chat.compatibility_ui.rasi')}</span>
                        <span className="text-sm font-medium text-gray-700">{boy_info.rasi}</span>
                    </div>
                )}
            </CardContent>
        </Card>

        {/* Girl Info */}
        <Card className="bg-gradient-to-br from-pink-50 to-white border-pink-100">
            <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-pink-800">
                    <User className="h-5 w-5 text-pink-600" />
                    {t('chat.compatibility_form.girl_details')}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-500">{t('chat.compatibility_ui.nakshatra')}</span>
                    <span className="text-lg font-bold text-pink-700">{girl_info.nakshatra}</span>
                </div>
                <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-500">{t('chat.compatibility_ui.pada')}</span>
                    <span className="text-sm font-medium text-gray-700">{girl_info.pada}</span>
                </div>
                {girl_info.rasi && (
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 flex items-center gap-1"><Moon className="w-3 h-3" /> {t('chat.compatibility_ui.rasi')}</span>
                        <span className="text-sm font-medium text-gray-700">{girl_info.rasi}</span>
                    </div>
                )}
            </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="south" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="south">{t('chat.compatibility_ui.south_indian')}</TabsTrigger>
            <TabsTrigger value="north">{t('chat.compatibility_ui.north_indian')}</TabsTrigger>
        </TabsList>
        
        <TabsContent value="south">
            {/* South Score Card */}
            <Card className="border-2 border-pink-100 overflow-hidden">
                <div className="bg-pink-50 p-6 text-center border-b border-pink-100">
                    <h3 className="text-lg font-medium text-gray-600 mb-2">{t('chat.compatibility_ui.total_compatibility_score')}</h3>
                    <div className={`text-5xl font-bold ${is_compatible_south ? 'text-green-600' : 'text-red-600'} mb-4`}>
                        {total_score_south} <span className="text-2xl text-gray-400">/ 10</span>
                    </div>
                    <div className="flex justify-center">
                        {is_compatible_south ? (
                            <span className="inline-flex items-center rounded-full border border-green-200 bg-green-100 px-4 py-1 text-lg font-semibold text-green-800 shadow-sm">
                                <CheckCircle className="w-5 h-5 mr-2" />
                                {t('chat.compatibility_ui.compatible')}
                            </span>
                        ) : (
                            <span className="inline-flex items-center rounded-full border border-red-200 bg-red-100 px-4 py-1 text-lg font-semibold text-red-800 shadow-sm">
                                <XCircle className="w-5 h-5 mr-2" />
                                {t('chat.compatibility_ui.not_compatible')}
                            </span>
                        )}
                    </div>
                </div>
                <CardContent className="p-6">
                    <h4 className="font-semibold text-gray-700 mb-4 flex items-center">
                        <Star className="w-4 h-4 mr-2 text-yellow-500" />
                        {t('chat.compatibility_ui.detailed_analysis')}
                    </h4>
                    <div className="grid md:grid-cols-2 gap-x-12 gap-y-1">
                        {Object.entries(details_south).map(([key, value]) => (
                            <div key={key}>
                                {renderDetailRow(key, value)}
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="north">
             {/* North Score Card */}
             <Card className="border-2 border-indigo-100 overflow-hidden">
                <div className="bg-indigo-50 p-6 text-center border-b border-indigo-100">
                    <h3 className="text-lg font-medium text-gray-600 mb-2">{t('chat.compatibility_ui.total_ashtakoota_score')}</h3>
                    <div className={`text-5xl font-bold ${getScoreColor(total_score_north)} mb-4`}>
                        {total_score_north} <span className="text-2xl text-gray-400">/ 36</span>
                    </div>
                     <div className="flex justify-center">
                        {total_score_north >= 18 ? (
                            <span className="inline-flex items-center rounded-full border border-green-200 bg-green-100 px-4 py-1 text-lg font-semibold text-green-800 shadow-sm">
                                <CheckCircle className="w-5 h-5 mr-2" />
                                {t('chat.compatibility_ui.good_match')}
                            </span>
                        ) : (
                            <span className="inline-flex items-center rounded-full border border-red-200 bg-red-100 px-4 py-1 text-lg font-semibold text-red-800 shadow-sm">
                                <XCircle className="w-5 h-5 mr-2" />
                                {t('chat.compatibility_ui.below_average')}
                            </span>
                        )}
                    </div>
                </div>
                <CardContent className="p-6">
                    <h4 className="font-semibold text-gray-700 mb-4 flex items-center">
                        <Star className="w-4 h-4 mr-2 text-indigo-500" />
                        {t('chat.compatibility_ui.ashtakoota_details')}
                    </h4>
                    <div className="grid md:grid-cols-2 gap-x-12 gap-y-1">
                         {details_north ? Object.entries(details_north).map(([key, value]) => (
                            <div key={key}>
                                {renderDetailRow(key, value)}
                            </div>
                        )) : <p>{t('chat.compatibility_ui.north_details_unavailable')}</p>}
                    </div>
                    
                    {renderNadiAlert()}
                </CardContent>
            </Card>
        </TabsContent>
      </Tabs>

      {renderDoshaSection()}

      <div className="flex justify-center gap-4">
        {onDiscussInAstro && (
          <Button onClick={onDiscussInAstro} variant="outline" className="px-8 py-6 text-lg">
            <User className="mr-2 h-5 w-5" />
            {t('chat.compatibility_ui.discuss_in_astro_mode')}
          </Button>
        )}
        <Button onClick={handleDownloadPDF} disabled={isGeneratingPdf} className="px-8 py-6 text-lg bg-blue-600 hover:bg-blue-700">
            {isGeneratingPdf ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : (
              <Download className="mr-2 h-5 w-5" />
            )}
            {isGeneratingPdf
              ? t('chat.dinacharya.actions.preparing_pdf', 'Preparing PDF')
              : t('chat.compatibility_ui.download_pdf_report')}
        </Button>
        <Button onClick={onReset} variant="outline" className="px-8 py-6 text-lg">
            {t('chat.compatibility_ui.check_another_match')}
        </Button>
      </div>
    </div>
  )
})

export default CompatibilityDisplay
